import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Loader2, CalendarDays, Clock, MapPin, MessageCircle, ImageDown } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL =
    "https://customer-assets.emergentagent.com/job_clinic-booking-86/artifacts/lfwsi76p_Logo_CE_fondoBlanco.png";

const SPANISH_DAYS = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];
const SPANISH_MONTHS = [
    "enero", "febrero", "marzo", "abril", "mayo", "junio",
    "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

function toISODate(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
}
function formatTime12(t) {
    const [hh, mm] = t.split(":").map(Number);
    const period = hh >= 12 ? "PM" : "AM";
    const h12 = hh % 12 === 0 ? 12 : hh % 12;
    return `${h12}:${String(mm).padStart(2, "0")} ${period}`;
}

// Next 7 open days according to the configurable schedule.
function buildDayChips(schedule) {
    const isClosed = (dow) => {
        const group = dow === 0 ? "sunday" : dow === 6 ? "saturday" : "weekday";
        return schedule ? !schedule[group]?.enabled : dow === 0;
    };
    const days = [];
    const now = new Date();
    for (let i = 0; days.length < 7 && i < 14; i++) {
        const d = new Date(now);
        d.setDate(now.getDate() + i);
        if (isClosed(d.getDay())) continue;
        days.push({
            iso: toISODate(d),
            label:
                i === 0
                    ? "Hoy"
                    : i === 1
                    ? "Mañana"
                    : `${SPANISH_DAYS[d.getDay()].slice(0, 3)} ${d.getDate()}`,
            full: `${SPANISH_DAYS[d.getDay()]} ${d.getDate()} de ${SPANISH_MONTHS[d.getMonth()]}`,
        });
    }
    return days;
}

export default function AvailabilityPage() {
    const [schedule, setSchedule] = useState(null);
    const days = useMemo(() => buildDayChips(schedule), [schedule]);
    const [selected, setSelected] = useState(days[0]);
    const [loading, setLoading] = useState(true);
    const [availability, setAvailability] = useState(null);
    const [promoProcs, setPromoProcs] = useState([]);
    const [downloadingImg, setDownloadingImg] = useState(false);

    useEffect(() => {
        if (days.length && !days.find((d) => d.iso === selected?.iso)) {
            setSelected(days[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [days]);

    const downloadImage = async () => {
        setDownloadingImg(true);
        try {
            const res = await axios.get(`${API}/appointments/availability/image.png`, {
                params: { date: selected.iso },
                responseType: "blob",
            });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: "image/png" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `disponibilidad_${selected.iso}.png`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Imagen descargada 📷");
        } catch {
            toast.error("No se pudo generar la imagen");
        } finally {
            setDownloadingImg(false);
        }
    };

    useEffect(() => {
        axios
            .get(`${API}/schedule`)
            .then((res) => setSchedule(res.data))
            .catch(() => {});
        axios
            .get(`${API}/procedures`)
            .then((res) =>
                setPromoProcs((res.data || []).filter((p) => p.promo_price != null))
            )
            .catch(() => {});
    }, []);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        axios
            .get(`${API}/appointments/availability`, { params: { date: selected.iso } })
            .then((res) => {
                if (!cancelled) setAvailability(res.data);
            })
            .catch(() => {
                if (!cancelled) setAvailability(null);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [selected]);

    const slots = availability?.available_slots || [];
    const morning = slots.filter((t) => parseInt(t.split(":")[0], 10) < 12);
    const afternoon = slots.filter((t) => parseInt(t.split(":")[0], 10) >= 12);

    return (
        <div className="min-h-screen flex flex-col bg-background">
            <header className="border-b border-border bg-card">
                <div className="max-w-3xl mx-auto px-5 sm:px-8 py-4 flex items-center gap-3">
                    <img
                        src={LOGO_URL}
                        alt="Logo Ciudad Enfermería"
                        className="w-11 h-11 rounded-full object-contain bg-white ring-1 ring-border"
                    />
                    <div className="leading-tight">
                        <p className="font-heading font-medium text-base">Ciudad Enfermería</p>
                        <p className="text-xs text-muted-foreground">Horarios disponibles</p>
                    </div>
                </div>
            </header>

            <main className="flex-1 py-8 sm:py-10">
                <div className="max-w-3xl mx-auto px-5 sm:px-8">
                    <div className="mb-6">
                        <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-2">
                            Disponibilidad en tiempo real
                        </p>
                        <h1 className="font-heading text-3xl sm:text-4xl font-medium tracking-tight">
                            Elige un día y mira los horarios libres
                        </h1>
                    </div>

                    {promoProcs.length > 0 && (
                        <div
                            className="mb-5 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
                            data-testid="availability-promo-banner"
                        >
                            🔥 <span className="font-semibold">Promoción:</span>{" "}
                            {promoProcs
                                .map(
                                    (p) =>
                                        `${p.name.replace(/\s*\(.*\)$/, "")} a $${p.promo_price} todos los días de ${formatTime12(p.promo_start)} a ${formatTime12(p.promo_end)}`
                                )
                                .join(" · ")}
                        </div>
                    )}

                    {/* Day chips */}
                    <div className="flex gap-2 overflow-x-auto pb-2 mb-6 -mx-1 px-1" data-testid="availability-day-chips">
                        {days.map((d) => (
                            <button
                                key={d.iso}
                                onClick={() => setSelected(d)}
                                className={`shrink-0 px-4 py-2 rounded-2xl text-sm font-medium border transition-colors ${
                                    selected.iso === d.iso
                                        ? "bg-primary text-primary-foreground border-primary"
                                        : "bg-card border-border hover:border-primary/40"
                                }`}
                                data-testid={`availability-day-${d.iso}`}
                            >
                                {d.label}
                            </button>
                        ))}
                    </div>

                    <Card className="rounded-3xl border-border bg-card p-6 sm:p-8 soft-shadow" data-testid="availability-results">
                        <p className="flex items-center gap-2 text-sm text-muted-foreground mb-5">
                            <CalendarDays className="h-4 w-4" />
                            {selected.full}
                        </p>

                        {loading ? (
                            <div className="flex items-center justify-center py-12 text-muted-foreground">
                                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                                Consultando horarios...
                            </div>
                        ) : !availability || !availability.is_open ? (
                            <p className="text-muted-foreground py-6 text-center" data-testid="availability-closed">
                                Este día el consultorio está cerrado.
                            </p>
                        ) : slots.length === 0 ? (
                            <p className="text-muted-foreground py-6 text-center" data-testid="availability-none">
                                Ya no quedan horarios disponibles para este día. Prueba otro día 🙏
                            </p>
                        ) : (
                            <div className="space-y-5">
                                {morning.length > 0 && (
                                    <div>
                                        <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-2">
                                            🌅 Mañana
                                        </p>
                                        <div className="flex flex-wrap gap-2">
                                            {morning.map((s) => (
                                                <span key={s} className="px-3 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium tabular-nums">
                                                    {formatTime12(s)}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {afternoon.length > 0 && (
                                    <div>
                                        <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-2">
                                            🌆 Tarde
                                        </p>
                                        <div className="flex flex-wrap gap-2">
                                            {afternoon.map((s) => (
                                                <span key={s} className="px-3 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium tabular-nums">
                                                    {formatTime12(s)}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="mt-8 pt-6 border-t border-border">
                            <Link to="/" data-testid="availability-book-link">
                                <Button className="w-full h-12 rounded-2xl text-base hover:-translate-y-0.5 transition-all">
                                    <Clock className="h-4 w-4 mr-2" />
                                    Reservar mi cita ahora
                                </Button>
                            </Link>
                            <Button
                                variant="outline"
                                onClick={downloadImage}
                                disabled={downloadingImg || loading}
                                className="w-full h-12 rounded-2xl text-base mt-3"
                                data-testid="availability-download-image-btn"
                            >
                                {downloadingImg ? (
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                ) : (
                                    <ImageDown className="h-4 w-4 mr-2" />
                                )}
                                Descargar imagen de horarios
                            </Button>
                            <p className="text-xs text-muted-foreground text-center mt-3">
                                Los horarios se actualizan en tiempo real conforme se reservan las citas.
                            </p>
                        </div>
                    </Card>

                    <p className="flex items-center justify-center gap-2 text-xs text-muted-foreground mt-6">
                        <MapPin className="h-3.5 w-3.5" />
                        Av. Educación 230 Int. 1, Col. Burócrata, San Luis Potosí
                    </p>
                </div>
            </main>

            {/* Floating WhatsApp button */}
            <a
                href={`https://wa.me/5214443067793?text=${encodeURIComponent(
                    "Hola 👋 Tengo una duda sobre los horarios disponibles antes de reservar mi cita."
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="fixed bottom-20 right-5 z-50 flex items-center gap-2 pl-4 pr-5 py-3 rounded-full bg-[#25D366] text-white font-medium text-sm shadow-lg hover:bg-[#1fb958] hover:-translate-y-0.5 transition-all"
                data-testid="availability-whatsapp-fab"
            >
                <MessageCircle className="h-5 w-5" />
                ¿Dudas? Escríbenos
            </a>
        </div>
    );
}
